// MarkDeliverableDatePopover — reusable confirmation popover for the
// three deliverable status transitions that carry a server-stamped
// business effective date: Submit, Accept, Reject. The server records
// `recordedAt` + `recordedBy` separately as audit truth; this popover
// only collects what the actor explicitly chooses (effective date and,
// for rejections, the required reason).
//
// Validation:
//   - effectiveDate: required, defaults to today, must be ≤ today (UTC).
//   - reason (Reject mode only): required, 1–2000 chars after trim.
//
// Modal-style overlay rendered by the host inside a backdrop. This
// component delegates dialog a11y (Escape, Tab focus trap, focus
// restoration, body scroll-lock) to `useFocusTrap`, matching the
// shared Modal primitive.

import { useEffect, useId, useRef, useState } from "react";
import { Button } from "../../../common";
import { useFocusTrap } from "../../../../hooks/useFocusTrap";

export type MarkAction = "submit" | "accept" | "reject";

interface MarkDeliverableDatePopoverProps {
  action: MarkAction;
  /** Pre-filled effective date — defaults to today (UTC) when undefined. */
  defaultEffectiveDate?: string;
  /** Pre-filled reason (Reject mode only). */
  defaultReason?: string;
  /** Pre-filled comment for the matching lifecycle stage. */
  defaultComment?: string;
  /** Earliest effective date the user may pick (YYYY-MM-DD). The host
   *  supplies the deliverable's stored `submittedAt` for accept/reject
   *  transitions so the stamped acceptedAt/rejectedAt can't predate the
   *  submission. Omit for the `submit` action (no anchor exists yet). */
  minEffectiveDate?: string;
  onConfirm: (input: {
    effectiveDate: string;
    reason?: string;
    comment?: string;
  }) => void;
  onCancel: () => void;
  /** When true, the Confirm button shows pending state. The host wires
   *  this from the mutation it dispatches on confirm. */
  isPending?: boolean;
  /** Surface error returned by the host's mutation so it sticks in the
   *  popover until the user retries or cancels. */
  errorMessage?: string | null;
  /** Used in aria-labels and on the Confirm button. */
  deliverableName: string;
}

const ACTION_LABELS: Record<MarkAction, { title: string; confirm: string }> = {
  submit: {
    title: "Mark deliverable submitted",
    confirm: "Mark submitted",
  },
  accept: {
    title: "Accept deliverable",
    confirm: "Accept",
  },
  reject: {
    title: "Reject deliverable",
    confirm: "Reject",
  },
};

function todayIsoUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function MarkDeliverableDatePopover({
  action,
  defaultEffectiveDate,
  defaultReason,
  defaultComment,
  minEffectiveDate,
  onConfirm,
  onCancel,
  isPending,
  errorMessage,
  deliverableName,
}: MarkDeliverableDatePopoverProps) {
  const today = todayIsoUtc();
  const [effectiveDate, setEffectiveDate] = useState<string>(
    defaultEffectiveDate ?? today,
  );
  const [reason, setReason] = useState<string>(defaultReason ?? "");
  const [comment, setComment] = useState<string>(defaultComment ?? "");
  const [dateError, setDateError] = useState<string | null>(null);
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [commentError, setCommentError] = useState<string | null>(null);
  const dateInputId = useId();
  const reasonInputId = useId();
  const commentInputId = useId();
  const dateInputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLFormElement>(null);

  const labels = ACTION_LABELS[action];
  const isReject = action === "reject";

  // Escape dismissal + Tab focus trap + focus restoration. The hook
  // also focuses the first focusable element on mount, so the explicit
  // dateInputRef.focus() below is only needed if a future caller hides
  // the date input. Keeping it as a defensive default.
  useFocusTrap(dialogRef, { onEscape: onCancel });

  useEffect(() => {
    dateInputRef.current?.focus();
  }, []);

  const handleConfirm = (e: React.FormEvent) => {
    e.preventDefault();
    let valid = true;

    if (!effectiveDate) {
      setDateError("Pick an effective date.");
      valid = false;
    } else if (effectiveDate > today) {
      setDateError("Effective date cannot be in the future.");
      valid = false;
    } else if (minEffectiveDate && effectiveDate < minEffectiveDate) {
      setDateError(
        action === "accept"
          ? "Accepted date must be on or after the submitted date."
          : action === "reject"
            ? "Rejected date must be on or after the submitted date."
            : "Effective date must be on or after the submitted date.",
      );
      valid = false;
    } else {
      setDateError(null);
    }

    if (isReject) {
      const trimmed = reason.trim();
      if (trimmed.length < 1) {
        setReasonError("A reason is required when rejecting.");
        valid = false;
      } else if (trimmed.length > 2000) {
        setReasonError("Reason must be 2000 characters or fewer.");
        valid = false;
      } else {
        setReasonError(null);
      }
    }

    // Comment is optional but if supplied must respect the 2000-char cap.
    const trimmedComment = comment.trim();
    if (trimmedComment.length > 2000) {
      setCommentError("Comment must be 2000 characters or fewer.");
      valid = false;
    } else {
      setCommentError(null);
    }

    if (!valid) return;

    onConfirm({
      effectiveDate,
      reason: isReject ? reason.trim() : undefined,
      // Omit the comment property entirely when blank so callers can
      // spread the result into a PUT payload without sending an empty
      // string to the backend.
      ...(trimmedComment.length > 0 ? { comment: trimmedComment } : {}),
    });
  };

  return (
    <form
      ref={dialogRef}
      data-testid={`mark-deliverable-popover-${action}`}
      role="dialog"
      aria-modal="true"
      aria-label={`${labels.title} — ${deliverableName}`}
      onSubmit={handleConfirm}
      className="absolute right-0 z-30 mt-1 w-72 space-y-3 rounded-md border border-slate-200 bg-white p-3 shadow-soft-lg dark:shadow-soft-lg-dark dark:border-slate-700 dark:bg-slate-800"
    >
      <p className="text-body-sm font-medium text-slate-900 dark:text-slate-100">
        {labels.title}
      </p>
      <div>
        <label htmlFor={dateInputId} className="label">
          Effective date
        </label>
        <input
          id={dateInputId}
          ref={dateInputRef}
          type="date"
          max={today}
          min={minEffectiveDate}
          value={effectiveDate}
          onChange={(e) => setEffectiveDate(e.target.value)}
          className="input w-full"
          disabled={isPending}
          aria-invalid={!!dateError}
        />
        {dateError && (
          <p
            className="mt-1 text-caption text-red-600 dark:text-red-400"
            role="alert"
          >
            {dateError}
          </p>
        )}
      </div>
      {isReject && (
        <div>
          <label htmlFor={reasonInputId} className="label">
            Reason
          </label>
          <textarea
            id={reasonInputId}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="input w-full min-h-[64px]"
            maxLength={2000}
            disabled={isPending}
            placeholder="Why is this deliverable being rejected?"
          />
          {reasonError && (
            <p
              className="mt-1 text-caption text-red-600 dark:text-red-400"
              role="alert"
            >
              {reasonError}
            </p>
          )}
        </div>
      )}
      <div>
        <label htmlFor={commentInputId} className="label">
          Comment <span className="text-slate-500">(optional)</span>
        </label>
        <textarea
          id={commentInputId}
          data-testid={`mark-deliverable-comment-${action}`}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          className="input w-full min-h-[56px]"
          maxLength={2000}
          disabled={isPending}
          placeholder={
            action === "submit"
              ? "Optional note for this submission"
              : action === "accept"
                ? "Optional acceptance note"
                : "Optional internal note (separate from the rejection reason above)"
          }
        />
        {commentError && (
          <p
            className="mt-1 text-caption text-red-600 dark:text-red-400"
            role="alert"
          >
            {commentError}
          </p>
        )}
      </div>
      {errorMessage && (
        <p
          className="text-caption text-red-600 dark:text-red-400"
          role="alert"
        >
          {errorMessage}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button type="submit" size="sm" loading={isPending}>
          {labels.confirm}
        </Button>
      </div>
    </form>
  );
}
